import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { ouvrirCompteEcole } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { getListSedecEcolesQueryKey } from "@/lib/backoffice-hooks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const ROLES = [
  { value: "direction", label: "Chef d'établissement" },
  { value: "econome", label: "Économe" },
  { value: "secretaire", label: "Secrétariat" },
];

/**
 * « Ouvrir un compte » sur une école : le SEDEC donne à une école nouvellement
 * créée sa première personne (en général le chef d'établissement), qui invite
 * ensuite son équipe depuis School Pro. Aucun mot de passe ne transite : la
 * personne reçoit un courriel et choisit le sien.
 */
export function OuvrirCompteEcole({ ecoleId, ecoleNom }: { ecoleId: string; ecoleNom: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", role: "direction" });
  const pret = form.firstName.trim() !== "" && form.lastName.trim() !== "" && /\S+@\S+\.\S+/.test(form.email);

  async function envoyer() {
    setBusy(true);
    try {
      const r = await ouvrirCompteEcole(ecoleId, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
      });
      toast.success(r.created
        ? `Compte ouvert : ${form.email.trim()} reçoit un courriel et choisit son mot de passe (« Première connexion ou mot de passe oublié ? »).`
        : `Le compte existant de ${form.email.trim()} reçoit l'accès à ${ecoleNom}.`);
      qc.invalidateQueries({ queryKey: getListSedecEcolesQueryKey() });
      setOpen(false);
      setForm({ firstName: "", lastName: "", email: "", role: "direction" });
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="text-xs px-2.5 py-1 border rounded inline-flex items-center gap-1 border-navy/20 text-navy hover:bg-slate-50"
        data-testid={`button-compte-${ecoleId}`}>
        <UserPlus className="w-3 h-3" /> Ouvrir un compte
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ouvrir un compte · {ecoleNom}</DialogTitle>
            <DialogDescription>
              La personne reçoit un courriel et choisit son mot de passe. Le chef d'établissement invite ensuite son équipe depuis School Pro.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="block text-sm">Prénom
              <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} data-testid="compte-prenom" />
            </label>
            <label className="block text-sm">Nom
              <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} data-testid="compte-nom" />
            </label>
            <label className="block text-sm">Adresse e-mail
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="compte-email" />
            </label>
            <label className="block text-sm">Rôle
              <select className="w-full border rounded-md h-10 px-3 text-sm" value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="compte-role">
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-3 py-2 border rounded">Annuler</button>
            <button type="button" onClick={envoyer} disabled={!pret || busy}
              className="text-sm px-3 py-2 rounded bg-navy text-white disabled:opacity-50" data-testid="compte-envoyer">
              Ouvrir le compte
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
